import type {PayloadAction} from '@reduxjs/toolkit'
import {createSlice} from '@reduxjs/toolkit'

import type {AgenticReport, ArtifactSummary} from '../../components/AskDevLens/types/events'
import type {
  AgenticProgressEvent,
  AgenticRunResult,
  AgenticRunTrace,
  AgenticToolTrace,
  ClarificationRequest,
  PlanLedger,
} from '../../components/AskDevLens/orchestration'
import {clearArtifactDataMemory} from '../../components/AskDevLens/harness'

export interface AgenticChatMessage {
  id: string
  role: 'user' | 'assistant'
  content: string
  dataEntries: ArtifactSummary[]
  summaryId?: string
}

export type AgenticChatProgressItem = AgenticProgressEvent

export interface AgenticPlanTurn {
  messageId: string
  progressItems: AgenticChatProgressItem[]
  plan: PlanLedger | null
}

interface AgenticChatState {
  messages: AgenticChatMessage[]
  progressItems: AgenticChatProgressItem[]
  currentPlan: PlanLedger | null
  planTurns: AgenticPlanTurn[]
  activePlanTurnMessageId: string | null
  isStreaming: boolean
  sessionId: string
  sessionIsDurable: boolean
  error: string | null
  lastTrace: AgenticRunTrace | null
  llmCallCount: number
  conversationSummary: string | null
  clarificationRequest: ClarificationRequest | null
  sessionLoading: boolean
  sessionLoadError: string | null
  reports: AgenticReport[]
  reportsError: string | null
}

function cloneProgressEvent(event: AgenticProgressEvent): AgenticChatProgressItem {
  return {
    ...event,
    input: event.input ? {...event.input} : undefined,
    output: event.output ? {...event.output} : undefined,
    toolCalls: event.toolCalls?.map(toolCall => ({
      ...toolCall,
      input: {...toolCall.input},
      output: toolCall.output ? {...toolCall.output} : undefined,
    })),
  }
}

function mergeToolCalls(
  existing: AgenticToolTrace[] | undefined,
  incoming: AgenticToolTrace[] | undefined,
): AgenticToolTrace[] | undefined {
  if (incoming === undefined) return existing
  const existingById = new Map((existing ?? []).map(toolCall => [toolCall.id, toolCall]))
  return incoming.map(toolCall => ({
    ...toolCall,
    subAgentContext: toolCall.subAgentContext ?? existingById.get(toolCall.id)?.subAgentContext,
    eventSequence: existingById.get(toolCall.id)?.eventSequence ?? toolCall.eventSequence,
  }))
}

function createInitialState(): AgenticChatState {
  return {
    messages: [],
    progressItems: [],
    currentPlan: null,
    planTurns: [],
    activePlanTurnMessageId: null,
    isStreaming: false,
    sessionId: crypto.randomUUID(),
    sessionIsDurable: false,
    error: null,
    lastTrace: null,
    llmCallCount: 0,
    conversationSummary: null,
    clarificationRequest: null,
    sessionLoading: false,
    sessionLoadError: null,
    reports: [],
    reportsError: null,
  }
}

export function classifyReportMessages(messages: AgenticChatMessage[], reports: AgenticReport[]): AgenticChatMessage[] {
  const reportIds = new Set(reports.map(report => report.reportId))
  const unmatchedReports = reports.filter(
    report => !messages.some(message => message.role === 'assistant' && message.summaryId === report.reportId),
  )
  let legacyReportIndex = 0

  return messages.map(message => {
    if (message.role !== 'assistant') return message
    if (message.summaryId !== undefined && reportIds.has(message.summaryId)) {
      return message
    }
    const legacyReport = unmatchedReports[legacyReportIndex]
    if (legacyReport !== undefined && legacyReport.markdown === message.content) {
      legacyReportIndex += 1
      return {...message, summaryId: legacyReport.reportId}
    }
    return message
  })
}

function applyHydratedReportsToMessages(
  messages: AgenticChatMessage[],
  reports: AgenticReport[],
): AgenticChatMessage[] {
  const hydratedById = new Map(reports.filter(report => report.hydrated).map(report => [report.reportId, report]))
  return classifyReportMessages(messages, reports).map(message => {
    const report = message.summaryId === undefined ? undefined : hydratedById.get(message.summaryId)
    return report === undefined
      ? message
      : {
          ...message,
          content: report.markdown,
          dataEntries: report.dataEntries,
        }
  })
}

function keepStoppedProgressVisible(items: AgenticChatProgressItem[]): AgenticChatProgressItem[] {
  return items.map(item => {
    if (item.status !== 'running') {
      return item
    }

    return {
      ...item,
      status: 'failed',
      summary: item.summary ?? 'Stopped before this step completed.',
      toolCalls: item.toolCalls?.map(toolCall =>
        toolCall.status === 'running'
          ? {
              ...toolCall,
              status: 'failed',
              output: toolCall.output ?? {error: 'Stopped before this tool call completed.'},
            }
          : toolCall,
      ),
    }
  })
}

function mergeProgressEvent(
  items: AgenticChatProgressItem[],
  event: AgenticChatProgressItem,
): AgenticChatProgressItem[] {
  const nextItems = [...items]
  const existingIndex = nextItems.findIndex(item => item.id === event.id)

  // When the primary thinking item transitions back to 'running', freeze any
  // existing completed summary as a permanent record so it stays visible as
  // commentary, then push a fresh 'running' item at the end of the list.
  if (event.agent === 'primary' && event.status === 'running' && existingIndex !== -1) {
    const existing = nextItems[existingIndex]
    if (existing.status === 'completed' && existing.summary) {
      nextItems[existingIndex] = {
        ...existing,
        id: `primary:summary:${crypto.randomUUID()}`,
      }
    } else {
      nextItems.splice(existingIndex, 1)
    }
    nextItems.push(event)
    return nextItems
  }

  if (existingIndex === -1) {
    if (event.kind === 'reasoning' && event.planStepId !== undefined) {
      const relatedExecutionIndex = nextItems.findIndex(
        item =>
          item.kind !== 'reasoning' &&
          (item.stepId === event.planStepId || item.stepId.endsWith(`:${event.planStepId}`)),
      )
      if (relatedExecutionIndex !== -1) {
        nextItems.splice(relatedExecutionIndex, 0, event)
        return nextItems
      }
    }
    nextItems.push(event)
    return nextItems
  }

  nextItems[existingIndex] = {
    ...nextItems[existingIndex],
    ...event,
    // Preserve the parallelGroupId established by the initial 'running' event.
    // Intermediate events emitted inside a sub-agent (e.g. tool-call updates)
    // don't carry parallelGroupId, so spreading them would silently erase it
    // and cause the item to fall out of its visual parallel group.
    parallelGroupId: event.parallelGroupId ?? nextItems[existingIndex].parallelGroupId,
    subAgentContext: event.subAgentContext ?? nextItems[existingIndex].subAgentContext,
    toolCalls: mergeToolCalls(nextItems[existingIndex].toolCalls, event.toolCalls),
  }
  return nextItems
}

export const agenticChatSlice = createSlice({
  name: 'agenticChat',
  initialState: createInitialState(),
  reducers: {
    beginRequest: (state, action: PayloadAction<string>) => {
      const messageId = `user-${crypto.randomUUID()}`
      state.messages.push({
        id: messageId,
        role: 'user',
        content: action.payload,
        dataEntries: [],
      })
      state.progressItems = []
      state.currentPlan = null
      state.activePlanTurnMessageId = messageId
      state.planTurns.push({messageId, progressItems: [], plan: null})
      state.isStreaming = true
      state.error = null
      state.sessionLoadError = null
      state.reportsError = null
    },
    applyProgress: (state, action: PayloadAction<AgenticProgressEvent>) => {
      const event = cloneProgressEvent(action.payload)
      state.progressItems = mergeProgressEvent(state.progressItems, event)
      const activeTurn = state.planTurns.find(turn => turn.messageId === state.activePlanTurnMessageId)
      if (activeTurn !== undefined) {
        activeTurn.progressItems = state.progressItems
      }
    },
    applyPlan: (state, action: PayloadAction<PlanLedger>) => {
      state.currentPlan = action.payload
      const activeTurn = state.planTurns.find(turn => turn.messageId === state.activePlanTurnMessageId)
      if (activeTurn !== undefined) activeTurn.plan = action.payload
    },
    restorePlanTurnProgress: (state, action: PayloadAction<{messageId: string; event: AgenticProgressEvent}>) => {
      const turn = state.planTurns.find(candidate => candidate.messageId === action.payload.messageId)
      if (turn === undefined) return
      const event = cloneProgressEvent(action.payload.event)
      turn.progressItems = mergeProgressEvent(turn.progressItems, event)
      if (turn.messageId === state.activePlanTurnMessageId) {
        state.progressItems = turn.progressItems
      }
    },
    restorePlanTurnPlan: (state, action: PayloadAction<{messageId: string; plan: PlanLedger}>) => {
      const turn = state.planTurns.find(candidate => candidate.messageId === action.payload.messageId)
      if (turn === undefined) return
      turn.plan = action.payload.plan
      if (turn.messageId === state.activePlanTurnMessageId) {
        state.currentPlan = action.payload.plan
      }
    },
    applyTrace: (state, action: PayloadAction<AgenticRunTrace>) => {
      state.lastTrace = action.payload
      state.currentPlan = action.payload.primaryContext.currentPlan
      const activeTurn = state.planTurns.find(turn => turn.messageId === state.activePlanTurnMessageId)
      if (activeTurn !== undefined) activeTurn.plan = action.payload.primaryContext.currentPlan
    },
    completeRequest: (state, action: PayloadAction<AgenticRunResult>) => {
      const result = action.payload
      const isReport = result.trace.primaryContext.currentPlan.steps.length > 0
      state.messages.push({
        id: `assistant-${result.summaryId}`,
        role: 'assistant',
        content: result.markdown,
        dataEntries: result.dataEntries,
        summaryId: result.summaryId,
      })
      if (isReport) {
        const report: AgenticReport = {
          reportId: result.summaryId,
          markdown: result.markdown,
          completedAt: new Date().toISOString(),
          artifactReferences: [],
          dataEntries: result.dataEntries,
          hydrated: true,
        }
        state.reports = [...state.reports.filter(item => item.reportId !== report.reportId), report]
      }
      // Keep the active progress items for orchestration consumers. The
      // turn-scoped copy remains in planTurns when the next request begins.
      state.isStreaming = false
      state.error = null
      state.lastTrace = result.trace
      state.currentPlan = result.trace.primaryContext.currentPlan
      const activeTurn = state.planTurns.find(turn => turn.messageId === state.activePlanTurnMessageId)
      if (activeTurn !== undefined) {
        activeTurn.plan = result.trace.primaryContext.currentPlan
        activeTurn.progressItems = state.progressItems
      }
      state.conversationSummary = result.conversationSummary ?? null
    },
    reconcileReports: (state, action: PayloadAction<AgenticReport[]>) => {
      const existingById = new Map(state.reports.map(report => [report.reportId, report]))
      state.reports = action.payload.map(report => {
        const existing = existingById.get(report.reportId)
        return existing?.hydrated
          ? {
              ...report,
              dataEntries: existing.dataEntries,
              hydrated: true,
            }
          : report
      })
      state.reportsError = null
      state.messages = applyHydratedReportsToMessages(state.messages, state.reports)
    },
    hydrateReportSucceeded: (state, action: PayloadAction<AgenticReport>) => {
      const index = state.reports.findIndex(report => report.reportId === action.payload.reportId)
      if (index >= 0) state.reports[index] = action.payload
      state.messages = applyHydratedReportsToMessages(state.messages, state.reports)
    },
    loadReportsFailed: (state, action: PayloadAction<string>) => {
      state.reportsError = action.payload
    },
    stopRequest: state => {
      state.isStreaming = false
      state.error = 'Response generation stopped.'
      state.progressItems = keepStoppedProgressVisible(state.progressItems)
      const activeTurn = state.planTurns.find(turn => turn.messageId === state.activePlanTurnMessageId)
      if (activeTurn !== undefined) activeTurn.progressItems = state.progressItems
    },
    cancelRequest: state => {
      state.isStreaming = false
      state.error = 'Response generation stopped.'
      state.progressItems = keepStoppedProgressVisible(state.progressItems)
      const activeTurn = state.planTurns.find(turn => turn.messageId === state.activePlanTurnMessageId)
      if (activeTurn !== undefined) activeTurn.progressItems = state.progressItems
    },
    failRequest: (state, action: PayloadAction<string>) => {
      state.isStreaming = false
      state.error = action.payload
    },
    resetError: state => {
      state.error = null
    },
    loadSessionStarted: (state, action: PayloadAction<string>) => {
      clearArtifactDataMemory()
      state.sessionId = action.payload
      state.sessionIsDurable = true
      state.messages = []
      state.progressItems = []
      state.currentPlan = null
      state.planTurns = []
      state.activePlanTurnMessageId = null
      state.isStreaming = false
      state.error = null
      state.lastTrace = null
      state.llmCallCount = 0
      state.conversationSummary = null
      state.clarificationRequest = null
      state.sessionLoading = true
      state.sessionLoadError = null
      state.reports = []
      state.reportsError = null
    },
    setDurableSessionId: (state, action: PayloadAction<string>) => {
      state.sessionId = action.payload
      state.sessionIsDurable = true
    },
    loadSessionSucceeded: (
      state,
      action: PayloadAction<{sessionId: string; messages: AgenticChatMessage[]; reports?: AgenticReport[]}>,
    ) => {
      clearArtifactDataMemory()
      state.sessionId = action.payload.sessionId
      state.sessionIsDurable = true
      state.reports = action.payload.reports ?? []
      state.messages = applyHydratedReportsToMessages(action.payload.messages, state.reports)
      state.progressItems = []
      state.currentPlan = null
      const latestUserMessage = [...state.messages].reverse().find(message => message.role === 'user')
      state.activePlanTurnMessageId = latestUserMessage?.id ?? null
      state.planTurns = state.messages
        .filter(message => message.role === 'user')
        .map(message => ({messageId: message.id, progressItems: [], plan: null}))
      state.isStreaming = false
      state.error = null
      state.lastTrace = null
      state.llmCallCount = 0
      state.conversationSummary = null
      state.clarificationRequest = null
      state.sessionLoading = false
      state.sessionLoadError = null
      state.reportsError = null
    },
    refreshSessionContentSucceeded: (
      state,
      action: PayloadAction<{sessionId: string; messages: AgenticChatMessage[]; reports: AgenticReport[]}>,
    ) => {
      if (action.payload.sessionId !== state.sessionId) return
      const existingById = new Map(state.reports.map(report => [report.reportId, report]))
      state.reports = action.payload.reports.map(report => {
        const existing = existingById.get(report.reportId)
        return existing?.hydrated
          ? {
              ...report,
              dataEntries: existing.dataEntries,
              hydrated: true,
            }
          : report
      })
      state.messages = applyHydratedReportsToMessages(action.payload.messages, state.reports)
      state.sessionLoading = false
      state.sessionLoadError = null
      state.reportsError = null
    },
    loadSessionFailed: (state, action: PayloadAction<string>) => {
      state.sessionLoading = false
      state.sessionLoadError = action.payload
    },
    incrementLlmCallCount: state => {
      state.llmCallCount += 1
    },
    resetLlmCallCount: state => {
      state.llmCallCount = 0
    },
    addSystemMessage: (state, action: PayloadAction<string>) => {
      state.messages.push({
        id: `system-${crypto.randomUUID()}`,
        role: 'assistant',
        content: action.payload,
        dataEntries: [],
      })
    },
    setClarificationRequest: (state, action: PayloadAction<ClarificationRequest>) => {
      state.clarificationRequest = action.payload
    },
    clearClarificationRequest: state => {
      state.clarificationRequest = null
    },
    resetConversation: () => {
      clearArtifactDataMemory()
      return createInitialState()
    },
  },
})

export const {
  beginRequest,
  applyProgress,
  applyPlan,
  restorePlanTurnProgress,
  restorePlanTurnPlan,
  applyTrace,
  completeRequest,
  reconcileReports,
  hydrateReportSucceeded,
  loadReportsFailed,
  stopRequest,
  cancelRequest,
  failRequest,
  resetError,
  loadSessionStarted,
  setDurableSessionId,
  loadSessionSucceeded,
  refreshSessionContentSucceeded,
  loadSessionFailed,
  incrementLlmCallCount,
  resetLlmCallCount,
  addSystemMessage,
  setClarificationRequest,
  clearClarificationRequest,
  resetConversation,
} = agenticChatSlice.actions

export const agenticChatReducer = agenticChatSlice.reducer
